import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Download, AlertCircle, Focus, RotateCcw, Loader2, Eye, EyeOff } from 'lucide-react';

type SharpenMode = 'standard' | 'unsharp' | 'edge';
type OutputFormat = 'png' | 'jpg';

interface Preset {
  label: string;
  amount: number;
  mode: SharpenMode;
  radius: number;
}

const PRESETS: Preset[] = [
  { label: 'Subtle', amount: 50, mode: 'standard', radius: 1 },
  { label: 'Normal', amount: 100, mode: 'standard', radius: 1 },
  { label: 'Strong', amount: 200, mode: 'standard', radius: 2 },
  { label: 'Maximum', amount: 300, mode: 'standard', radius: 2 },
];

const MODES: { key: SharpenMode; label: string; desc: string }[] = [
  { key: 'standard', label: 'Standard', desc: '3x3/5x5 convolution kernel' },
  { key: 'unsharp', label: 'Unsharp Mask', desc: 'Blur then subtract' },
  { key: 'edge', label: 'Edge Enhance', desc: 'Laplacian edge emphasis' },
];

const SharpenImage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [mode, setMode] = useState<SharpenMode>('standard');
  const [amount, setAmount] = useState(100);
  const [radius, setRadius] = useState<1 | 2>(1);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [showBefore, setShowBefore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    setFile(f);
    setError(null);
    setAmount(100);
    setMode('standard');
    setRadius(1);
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setOriginalPreview(url);
      const img = new window.Image();
      img.onload = () => setImgElement(img);
      img.onerror = () => setError('Failed to load image.');
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const applyStandardSharpen = useCallback((data: Uint8ClampedArray, w: number, h: number, strength: number, rad: number): Uint8ClampedArray => {
    const output = new Uint8ClampedArray(data.length);
    output.set(data);

    if (rad === 1) {
      // 3x3 kernel
      const center = 1 + 4 * strength;
      const edge = -strength;
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          for (let c = 0; c < 3; c++) {
            const idx = (y * w + x) * 4 + c;
            const val =
              data[((y - 1) * w + x) * 4 + c] * edge +
              data[(y * w + (x - 1)) * 4 + c] * edge +
              data[(y * w + x) * 4 + c] * center +
              data[(y * w + (x + 1)) * 4 + c] * edge +
              data[((y + 1) * w + x) * 4 + c] * edge;
            output[idx] = Math.min(255, Math.max(0, val));
          }
          output[(y * w + x) * 4 + 3] = data[(y * w + x) * 4 + 3];
        }
      }
    } else {
      // 5x5 kernel (extended sharpening)
      const kernel = [
        [0, 0, -1, 0, 0],
        [0, -1, -2, -1, 0],
        [-1, -2, 0, -2, -1],
        [0, -1, -2, -1, 0],
        [0, 0, -1, 0, 0],
      ];
      // Calculate center value to normalize
      let sum = 0;
      for (let ky = 0; ky < 5; ky++) for (let kx = 0; kx < 5; kx++) sum += kernel[ky][kx];
      kernel[2][2] = 1 - sum * strength;
      // Scale outer values
      for (let ky = 0; ky < 5; ky++) {
        for (let kx = 0; kx < 5; kx++) {
          if (ky !== 2 || kx !== 2) kernel[ky][kx] *= strength;
        }
      }

      for (let y = 2; y < h - 2; y++) {
        for (let x = 2; x < w - 2; x++) {
          for (let c = 0; c < 3; c++) {
            let val = 0;
            for (let ky = -2; ky <= 2; ky++) {
              for (let kx = -2; kx <= 2; kx++) {
                val += data[((y + ky) * w + (x + kx)) * 4 + c] * kernel[ky + 2][kx + 2];
              }
            }
            output[(y * w + x) * 4 + c] = Math.min(255, Math.max(0, val));
          }
          output[(y * w + x) * 4 + 3] = data[(y * w + x) * 4 + 3];
        }
      }
    }
    return output;
  }, []);

  const applyUnsharpMask = useCallback((data: Uint8ClampedArray, w: number, h: number, strength: number): Uint8ClampedArray => {
    const output = new Uint8ClampedArray(data.length);
    // Simple box blur for the blurred copy
    const blurred = new Uint8ClampedArray(data.length);
    blurred.set(data);

    const blurRadius = 2;
    for (let y = blurRadius; y < h - blurRadius; y++) {
      for (let x = blurRadius; x < w - blurRadius; x++) {
        for (let c = 0; c < 3; c++) {
          let sum = 0;
          let count = 0;
          for (let ky = -blurRadius; ky <= blurRadius; ky++) {
            for (let kx = -blurRadius; kx <= blurRadius; kx++) {
              sum += data[((y + ky) * w + (x + kx)) * 4 + c];
              count++;
            }
          }
          blurred[(y * w + x) * 4 + c] = sum / count;
        }
        blurred[(y * w + x) * 4 + 3] = data[(y * w + x) * 4 + 3];
      }
    }

    // result = original + amount * (original - blurred)
    for (let i = 0; i < data.length; i += 4) {
      for (let c = 0; c < 3; c++) {
        const orig = data[i + c];
        const blur = blurred[i + c];
        const val = orig + strength * (orig - blur);
        output[i + c] = Math.min(255, Math.max(0, val));
      }
      output[i + 3] = data[i + 3];
    }
    return output;
  }, []);

  const applyEdgeEnhance = useCallback((data: Uint8ClampedArray, w: number, h: number, strength: number): Uint8ClampedArray => {
    const output = new Uint8ClampedArray(data.length);
    output.set(data);

    // Laplacian kernel for edge detection, then blend with original
    // Laplacian: [[0,-1,0],[-1,4,-1],[0,-1,0]]
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        for (let c = 0; c < 3; c++) {
          const idx = (y * w + x) * 4 + c;
          const laplacian =
            -data[((y - 1) * w + x) * 4 + c] +
            -data[(y * w + (x - 1)) * 4 + c] +
            4 * data[(y * w + x) * 4 + c] +
            -data[(y * w + (x + 1)) * 4 + c] +
            -data[((y + 1) * w + x) * 4 + c];
          // Blend: original + strength * laplacian
          const val = data[idx] + strength * laplacian;
          output[idx] = Math.min(255, Math.max(0, val));
        }
        output[(y * w + x) * 4 + 3] = data[(y * w + x) * 4 + 3];
      }
    }
    return output;
  }, []);

  const applySharpen = useCallback((canvas: HTMLCanvasElement, img: HTMLImageElement) => {
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const w = canvas.width;
    const h = canvas.height;
    const strength = amount / 100;

    let resultData: Uint8ClampedArray;
    switch (mode) {
      case 'unsharp':
        resultData = applyUnsharpMask(imageData.data, w, h, strength);
        break;
      case 'edge':
        resultData = applyEdgeEnhance(imageData.data, w, h, strength);
        break;
      default:
        resultData = applyStandardSharpen(imageData.data, w, h, strength, radius);
        break;
    }

    const result = new ImageData(resultData, w, h);
    ctx.putImageData(result, 0, 0);
  }, [amount, mode, radius, applyStandardSharpen, applyUnsharpMask, applyEdgeEnhance]);

  // Live preview with debounce
  useEffect(() => {
    if (!imgElement || !previewCanvasRef.current) return;
    setIsProcessing(true);
    const timer = setTimeout(() => {
      applySharpen(previewCanvasRef.current!, imgElement);
      setIsProcessing(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [imgElement, amount, mode, radius, applySharpen]);

  const downloadResult = () => {
    if (!imgElement || !file) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    applySharpen(canvas, imgElement);
    const mimeType = outputFormat === 'jpg' ? 'image/jpeg' : 'image/png';
    const quality = outputFormat === 'jpg' ? 0.92 : undefined;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const name = file.name.replace(/\.[^.]+$/, '');
      link.download = `${name}_sharpened.${outputFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }, mimeType, quality);
  };

  const applyPreset = (preset: Preset) => {
    setAmount(preset.amount);
    setMode(preset.mode);
    setRadius(preset.radius as 1 | 2);
  };

  const reset = () => {
    setFile(null);
    setOriginalPreview(null);
    setImgElement(null);
    setAmount(100);
    setMode('standard');
    setRadius(1);
    setShowBefore(false);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-6">
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-cyan-100 rounded-full">
            <Focus className="w-8 h-8 text-cyan-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Sharpen Image</h2>
        <p className="text-gray-600">
          Enhance image clarity with multiple sharpening algorithms and presets.
        </p>
      </div>

      {/* Upload */}
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-cyan-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop an image here or click to select
          </h3>
          <p className="text-gray-600">Supports PNG, JPG, WebP and more</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {file && imgElement && (
        <div className="space-y-4">
          {/* Mode Tabs */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="text-sm font-medium text-gray-700 mb-3 block">Sharpening Mode</label>
            <div className="grid grid-cols-3 gap-2">
              {MODES.map((m) => (
                <button
                  key={m.key}
                  onClick={() => setMode(m.key)}
                  className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                    mode === m.key
                      ? 'bg-cyan-600 text-white shadow-sm'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <div className="font-semibold">{m.label}</div>
                  <div className={`text-xs mt-0.5 ${mode === m.key ? 'text-cyan-100' : 'text-gray-500'}`}>
                    {m.desc}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-4">
            {/* Amount Slider */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Amount</label>
                <span className="text-lg font-bold text-cyan-600">{amount}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="300"
                step="5"
                value={amount}
                onChange={(e) => setAmount(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-cyan-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0%</span>
                <span>150%</span>
                <span>300%</span>
              </div>
            </div>

            {/* Radius Control (Standard mode only) */}
            {mode === 'standard' && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Kernel Size</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setRadius(1)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      radius === 1
                        ? 'bg-cyan-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    3x3 (Radius 1)
                  </button>
                  <button
                    onClick={() => setRadius(2)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      radius === 2
                        ? 'bg-cyan-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    5x5 (Radius 2)
                  </button>
                </div>
              </div>
            )}

            {/* Presets */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Presets</label>
              <div className="flex gap-2">
                {PRESETS.map((p) => (
                  <button
                    key={p.label}
                    onClick={() => applyPreset(p)}
                    className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-white text-gray-700 hover:bg-cyan-50 hover:text-cyan-700 border border-gray-200 transition-colors"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Processing indicator */}
            {isProcessing && (
              <div className="flex items-center gap-2 text-cyan-600 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Processing...</span>
              </div>
            )}
          </div>

          {/* Preview */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-700">
                {showBefore ? 'Original' : 'Sharpened Preview'}
              </h3>
              <button
                onClick={() => setShowBefore(!showBefore)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {showBefore ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showBefore ? 'Show After' : 'Show Before'}
              </button>
            </div>
            <div className="bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center min-h-[250px]">
              {showBefore && originalPreview ? (
                <img src={originalPreview} alt="Original" className="max-w-full max-h-[500px] object-contain" />
              ) : (
                <canvas ref={previewCanvasRef} className="max-w-full max-h-[500px] object-contain" />
              )}
            </div>
          </div>

          {/* Download Options */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">Output Format</label>
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setOutputFormat('png')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  outputFormat === 'png'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                PNG
              </button>
              <button
                onClick={() => setOutputFormat('jpg')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  outputFormat === 'jpg'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                JPG
              </button>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={downloadResult}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-cyan-600 text-white font-medium rounded-xl hover:bg-cyan-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              Download Sharpened Image
            </button>
            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharpenImage;
